import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from './Icon';

type Props = {
  opened: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: IconName;
  size?: number;
  footer?: ReactNode;
  children: ReactNode;
};

/**
 * Drop-in replacement for Mantine's Drawer. The reason we roll our own
 * is that Mantine v7 keeps overriding the padding/layout we set on the
 * header and body even via the `styles` prop — debugging the cascade
 * fight isn't worth the time vs. shipping a 60-line component that does
 * exactly what we want.
 *
 * Behaviour:
 *   • Renders into a portal anchored to document.body.
 *   • Closes on overlay click and on the Escape key.
 *   • Disables body scroll while open.
 *   • Slides in from the right with a 220 ms CSS transform.
 */
export function MafaDrawer({
  opened,
  onClose,
  title,
  subtitle,
  icon,
  size = 520,
  footer,
  children,
}: Props) {
  useEffect(() => {
    if (!opened) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [opened, onClose]);

  if (!opened) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'flex',
        justifyContent: 'flex-end',
        pointerEvents: 'auto',
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'oklch(0.25 0.02 215 / .42)',
          backdropFilter: 'blur(2px)',
          animation: 'mafaFade .2s ease both',
        }}
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          position: 'relative',
          zIndex: 1,
          width: size,
          maxWidth: '92vw',
          height: '100%',
          background: 'var(--paper)',
          boxShadow: 'var(--sh-lg)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'mafaSlideIn .26s cubic-bezier(.3,.9,.3,1) both',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '20px 24px',
            borderBottom: '1px solid var(--line)',
            background: 'var(--panel)',
            flex: '0 0 auto',
          }}
        >
          {icon && (
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                display: 'grid',
                placeItems: 'center',
                background: 'var(--accent-tint)',
                color: 'var(--accent-2)',
                border: '1px solid var(--accent-tint-2)',
                flex: '0 0 auto',
              }}
            >
              <Icon name={icon} size={19} />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontWeight: 800,
                fontSize: 17,
                letterSpacing: '-.02em',
                color: 'var(--ink)',
              }}
            >
              {title}
            </div>
            {subtitle && (
              <div
                style={{
                  fontSize: 12.5,
                  color: 'var(--ink-3)',
                  fontWeight: 600,
                  marginTop: 3,
                }}
              >
                {subtitle}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              border: '1px solid var(--line)',
              background: 'var(--panel)',
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
              color: 'var(--ink-2)',
              boxShadow: 'var(--sh-sm)',
              flex: '0 0 auto',
            }}
          >
            <Icon name="x" size={18} />
          </button>
        </header>

        <div
          style={{
            flex: '1 1 auto',
            overflowY: 'auto',
            padding: '24px 26px',
            minHeight: 0,
            background: 'var(--paper)',
          }}
        >
          {children}
        </div>

        {footer && (
          <footer
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              padding: '16px 24px',
              borderTop: '1px solid var(--line)',
              background: 'var(--panel)',
              flex: '0 0 auto',
            }}
          >
            {footer}
          </footer>
        )}
      </aside>

      <style>{`
        @keyframes mafaFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes mafaSlideIn { from { transform: translateX(100%); } to { transform: none; } }
      `}</style>
    </div>,
    document.body,
  );
}
